import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFeatures, useSession } from "../hooks/index.js";
import { usePositions } from "../hooks/positions.jsx";
import { useOpenStock } from "../lib/navigation.js";
import { logout, searchStocks } from "../lib/api.js";
import Icon from "./ui/Icon.jsx";
import BrandMark from "./ui/BrandMark.jsx";
import Modal from "./ui/Modal.jsx";

const SECTIONS = [
  { key: "stocks", path: "/stocks", label: "Stocks", short: "Stocks", icon: "chart", description: "Find quality for the long term" },
  { key: "swing", path: "/swing", label: "Swing", short: "Swing", icon: "trendUp", description: "Explore today's opportunities" },
  { key: "investments", path: "/investments", label: "My investments", short: "Mine", icon: "gauge", description: "Track what you actually bought", always: true },
  { key: "funds", path: "/funds", label: "Funds", short: "Funds", icon: "leaf", description: "Build a steadier investing habit" },
  { key: "users", path: "/users", label: "People", short: "People", icon: "users", description: "Manage workspace access", admin: true },
  { key: "usage", path: "/usage", label: "Activity", short: "Activity", icon: "clock", description: "Understand workspace usage", admin: true }
];

function NavigationItems({ items, active, onNavigate, compact = false, badges = {} }) {
  return items.map((item) => (
    <Link
      key={item.key}
      to={item.path}
      className={`workspace-nav-link${active === item.key ? " active" : ""}`}
      aria-current={active === item.key ? "page" : undefined}
      aria-label={item.label}
      title={item.label}
      onClick={onNavigate}
    >
      <Icon name={item.icon} size={20} strokeWidth={1.8} />
      <span className="workspace-nav-copy">
        <strong>{compact ? item.short : item.label}</strong>
        {!compact && <span>{item.description}</span>}
      </span>
      {badges[item.key] > 0 && (
        <span className="nav-badge" aria-label={`${badges[item.key]} to exit`}>{badges[item.key]}</span>
      )}
      {!compact && <Icon name="chevronRight" className="workspace-nav-arrow" />}
    </Link>
  ));
}

export default function WorkspaceLayout({ children }) {
  const features = useFeatures();
  const { admin } = useSession();
  const { pendingExits } = usePositions();
  const badges = { investments: pendingExits.length };
  const location = useLocation();
  const openStock = useOpenStock();
  const [collapsed, setCollapsed] = useState(false);
  const [dialog, setDialog] = useState(null);
  const main = useRef(null);
  const pathname = location.state?.background?.pathname ?? location.pathname;
  const previousPath = useRef(pathname);
  const items = SECTIONS.filter((item) => (item.always ? true : item.admin ? admin : features[item.key]));
  const primary = items.filter((item) => !item.admin);
  const administration = items.filter((item) => item.admin);
  const current = SECTIONS.find((item) => item.path === pathname)
    ?? (pathname.startsWith("/stock/") ? SECTIONS.find((item) => item.key === "stocks") : null);
  const home = primary[0]?.path ?? "/stocks";

  useEffect(() => {
    document.title = `${current?.label ?? "Workspace"} | Pakka Paisa`;
    if (previousPath.current !== pathname) {
      previousPath.current = pathname;
      setDialog(null);
      window.scrollTo({ top: 0 });
      main.current?.focus({ preventScroll: true });
    }
  }, [pathname, current?.label]);

  useEffect(() => {
    if (!features.stocks) return;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k" && !document.querySelector("dialog[open]")) {
        event.preventDefault();
        setDialog("search");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [features.stocks]);

  const closeDialog = () => setDialog(null);
  const navigation = (
    <>
      <p className="workspace-nav-label">Research</p>
      <NavigationItems items={primary} active={current?.key} onNavigate={closeDialog} badges={badges} />
      {administration.length > 0 && (
        <>
          <p className="workspace-nav-label administration-label">Workspace</p>
          <NavigationItems items={administration} active={current?.key} onNavigate={closeDialog} />
        </>
      )}
    </>
  );

  return (
    <div className={`workspace${collapsed ? " sidebar-collapsed" : ""}`}>
      <a href="#main-content" className="skip-link">Skip to main content</a>

      <aside className="workspace-sidebar" aria-label="Workspace sidebar">
        <Link className="workspace-brand" to={home} aria-label="Pakka Paisa home">
          <BrandMark />
          <span>Pakka Paisa<small>Invest with perspective.</small></span>
        </Link>
        <nav className="workspace-navigation" aria-label="Main navigation">{navigation}</nav>
        <div className="sidebar-footer">
          <div className="sidebar-note">
            <Icon name="leaf" size={24} strokeWidth={1.5} />
            <strong>Clarity over noise.</strong>
            <p>Thoughtful research for your next financial decision.</p>
          </div>
          <button type="button" className="sidebar-guide" onClick={() => setDialog("guide")} title="Workspace guide">
            <Icon name="book" size={19} />
            <span>How to use this workspace</span>
          </button>
        </div>
      </aside>

      <div className="workspace-content">
        <header className="workspace-topbar">
          <button
            type="button"
            className="icon-btn desktop-control"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            <Icon name="sidebar" size={20} strokeWidth={1.75} />
          </button>
          <button type="button" className="icon-btn mobile-control" aria-label="Open navigation" aria-haspopup="dialog" onClick={() => setDialog("navigation")}>
            <Icon name="menu" />
          </button>
          <div className="workspace-breadcrumb">
            <strong>{current?.label ?? "Page not found"}</strong>
          </div>
          <Link className="workspace-mobile-brand" to={home}><BrandMark /><span>Pakka Paisa</span></Link>
          <div className="workspace-topbar-actions">
            {features.stocks && (
              <button type="button" className="workspace-search" onClick={() => setDialog("search")} aria-label="Search companies or symbols" aria-haspopup="dialog" aria-keyshortcuts="Control+k Meta+k">
                <Icon name="search" size={18} />
                <span>Search companies</span>
                <kbd>Ctrl / &#8984; K</kbd>
              </button>
            )}
            <button type="button" className="icon-btn guide-trigger" aria-label="Open workspace guide" aria-haspopup="dialog" onClick={() => setDialog("guide")}>
              <Icon name="book" />
            </button>
            <AccountMenu />
          </div>
        </header>

        <main id="main-content" className="workspace-main" ref={main} tabIndex={-1}>{children}</main>
      </div>

      <nav className="workspace-bottom-nav" aria-label="Mobile navigation">
        <NavigationItems items={primary} active={current?.key} compact badges={badges} />
        {administration.length > 0 && (
          <button type="button" className={`workspace-nav-link${current?.admin ? " active" : ""}`} onClick={() => setDialog("navigation")} aria-label="More workspace navigation" aria-haspopup="dialog">
            <Icon name="menu" size={20} />
            <span className="workspace-nav-copy"><strong>More</strong></span>
          </button>
        )}
      </nav>

      {dialog === "navigation" && (
        <Modal label="Workspace navigation" onClose={closeDialog} className="navigation-dialog">
          <div className="navigation-dialog-body">
            <div className="modal-heading">
              <Link className="workspace-brand" to={home} onClick={closeDialog}><BrandMark /><span>Pakka Paisa</span></Link>
              <button type="button" className="icon-btn" aria-label="Close navigation" onClick={closeDialog}><Icon name="close" /></button>
            </div>
            <nav aria-label="All workspace sections">{navigation}</nav>
            <button type="button" className="sidebar-guide" onClick={() => setDialog("guide")}><Icon name="book" />Workspace guide</button>
          </div>
        </Modal>
      )}
      {dialog === "search" && (
        <StockSearch onClose={closeDialog} onOpen={(symbol) => { closeDialog(); openStock(symbol); }} />
      )}
      {dialog === "guide" && <WorkspaceGuide items={primary.filter((item) => item.key !== "home")} onClose={closeDialog} />}
    </div>
  );
}

function AccountMenu() {
  const { username, admin } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const trigger = useRef(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!username) return null;

  const signOut = async () => {
    setBusy(true);
    setError(null);
    try {
      await logout();
      window.dispatchEvent(new Event("auth:expired"));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acct" ref={ref} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <button
        ref={trigger}
        type="button"
        className="account-trigger"
        aria-label={`Account: ${username}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="account-avatar">{username.slice(0, 1).toUpperCase()}</span>
        <Icon name="chevronDown" size={13} />
      </button>
      {open && (
        <div className="acct-menu" id={id}>
          <div className="acct-who">
            <span className="muted-sm">Signed in as</span>
            <strong>{username}</strong>
            {admin && <span className="self-tag">Workspace admin</span>}
          </div>
          {error && <p className="account-error" role="alert">{error}</p>}
          <button type="button" className="acct-item" disabled={busy} onClick={signOut}>
            <Icon name="signOut" />
            {busy ? "Signing out..." : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}

function StockSearch({ onClose, onOpen }) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const input = useRef(null);
  const term = query.trim();
  const symbol = term.toUpperCase();
  const validSymbol = /^[A-Z0-9][A-Z0-9&.-]{0,29}$/.test(symbol);

  useEffect(() => { input.current?.focus(); }, []);
  useEffect(() => {
    setMatches([]);
    setError(null);
    setLoading(term.length >= 2);
    if (term.length < 2) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchStocks(term, controller.signal).then(
        (rows) => { if (!controller.signal.aborted) { setMatches(rows.slice(0, 7)); setLoading(false); } },
        (err) => { if (!controller.signal.aborted) { setError(err.message); setLoading(false); } }
      );
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [term]);

  const submit = (event) => {
    event.preventDefault();
    const match = matches.find((row) => row.ticker === symbol) ?? matches[0];
    if (match) onOpen(match.ticker);
    else if (validSymbol) onOpen(symbol);
    else setError("Choose a matching company, or enter an NSE symbol.");
  };

  return (
    <Modal label="Find a company" onClose={onClose} className="search-dialog">
      <div className="modal-body">
        <div className="modal-heading">
          <div><p className="eyebrow">Stock research</p><h2>Find a company</h2></div>
          <button type="button" className="icon-btn" aria-label="Close search" onClick={onClose}><Icon name="close" /></button>
        </div>
        <form className="lookup-form" onSubmit={submit}>
          <label className="lookup-input">
            <Icon name="search" size={21} />
            <input ref={input} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Company name or NSE symbol" aria-label="Company name or NSE symbol" autoComplete="off" spellCheck={false} />
          </label>
          {term && <button className="btn primary lookup-submit" type="submit" disabled={!matches.length && !validSymbol}>Open {matches.length ? "report" : symbol}<Icon name="arrowRight" /></button>}
        </form>
        <div className="lookup-status" role="status">
          {loading ? "Looking for matching companies..." : term.length >= 2 && !matches.length && !error ? "No matches found. You can still open an exact NSE symbol." : !term ? "Search by name, or open a report directly with its symbol." : ""}
        </div>
        {error && <p className="signin-error" role="alert">Search is unavailable: {error} You can still enter an exact NSE symbol.</p>}
        {matches.length > 0 && (
          <ul className="lookup-results" aria-label="Matching companies">
            {matches.map((row) => (
              <li key={row.sid ?? row.ticker}>
                <button type="button" onClick={() => onOpen(row.ticker)}>
                  <span className="lookup-symbol">{row.ticker}</span>
                  <span className="lookup-name">{row.name}</span>
                  <Icon name="arrowRight" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {!term && (
          <div className="lookup-examples">
            <span>Try a symbol</span>
            {["TCS", "RELIANCE", "INFY"].map((value) => <button type="button" key={value} onClick={() => setQuery(value)}>{value}<Icon name="arrowRight" /></button>)}
          </div>
        )}
        <p className="modal-footnote"><kbd>Esc</kbd> to close. Reports open over your current page, so you never lose your place.</p>
      </div>
    </Modal>
  );
}

function WorkspaceGuide({ items, onClose }) {
  return (
    <Modal label="Workspace guide" onClose={onClose} className="guide-dialog">
      <div className="modal-body">
        <div className="modal-heading">
          <div><p className="eyebrow">A good place to start</p><h2>Your money. More perspective.</h2></div>
          <button type="button" className="icon-btn" aria-label="Close guide" onClick={onClose}><Icon name="close" /></button>
        </div>
        <p className="guide-intro">Start with a goal. Make one setup decision at a time, review your choices, then select a result to understand it.</p>
        <div className="guide-sections">
          {items.map((item) => (
            <Link key={item.key} to={item.path} onClick={onClose}>
              <span className="guide-icon"><Icon name={item.icon} size={24} /></span>
              <span><strong>{item.label}</strong><span>{item.description}. {item.key === "stocks" ? "Choose an investing style, run a screen, and open any company for its full report." : item.key === "swing" ? "Pick a strategy and a universe, then keep today's setups separate from the historical evidence." : "Choose your risk preference, compare a shortlist, and explore a monthly investment plan."}</span></span>
              <Icon name="arrowRight" />
            </Link>
          ))}
        </div>
        <p className="surface-note"><Icon name="info" />Screening is a starting point, not investment advice. Missing data stays visible, and past performance is never a promise.</p>
      </div>
    </Modal>
  );
}
